import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaShoppingCart,
  FaBoxOpen,
  FaSignOutAlt,
  FaChevronDown,
} from "react-icons/fa";
import { HiMenu, HiX } from "react-icons/hi";
import logo from "../assets/logonew.png";
import SummaryApi from "../common";

const Header = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);

  const [customerName, setCustomerName] = useState(
    sessionStorage.getItem("customer_name") || ""
  );
  const [customerImage, setCustomerImage] = useState(
    sessionStorage.getItem("customer_image") || ""
  );
  const [cartCount, setCartCount] = useState(0); // Default to 0
  const [isLoading, setIsLoading] = useState(false);

  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const isLoggedIn = !!sessionStorage.getItem("customer_id");

  const navLinks = [
    { name: "Home", path: "/" },
    { name: "About Us", path: "/about" },
    { name: "Products", path: "/product" },
    { name: "FAQs", path: "/faqs" },
    { name: "Contact Us", path: "/contact" },
  ];

  // --- 1. Fetch Cart Count (UPDATED) ---
  const fetchCartCount = async () => {
    const customerId = sessionStorage.getItem("customer_id");

    // If not logged in, count is always 0
    if (!customerId) {
      setCartCount(0);
      return;
    }

    try {
      const response = await fetch(SummaryApi.getCartCount.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: SummaryApi.bearerToken,
        },
        body: JSON.stringify({ customer_id: customerId }),
      });

      const data = await response.json();

      // LOGIC UPDATE: Handle success and fallback to 0
      if (data.status && data.cart_count !== undefined) {
        setCartCount(data.cart_count);
      } else {
        setCartCount(0); // Explicitly set to 0 if status is false or count missing
      }
    } catch (error) {
      console.error("Error fetching cart count:", error);
      setCartCount(0); // Error fallback
    }
  };

  // --- 2. Fetch Customer Details ---
  const fetchCustomerDetails = async () => {
    const customerId = sessionStorage.getItem("customer_id");
    if (!customerId) return;

    if (!customerName) setIsLoading(true);

    try {
      const response = await fetch(SummaryApi.customerDetailsById.url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: SummaryApi.bearerToken,
        },
        body: JSON.stringify({ customer_id: customerId }),
      });

      const data = await response.json();

      if ((data.status || data.success) && data.data) {
        const details = Array.isArray(data.data) ? data.data[0] : data.data;
        const nameToDisplay = details.full_name || details.customer_name || "";
        const imageToDisplay = details.customer_image || "";

        if (nameToDisplay)
          sessionStorage.setItem("customer_name", nameToDisplay);
        if (imageToDisplay)
          sessionStorage.setItem("customer_image", imageToDisplay);

        setCustomerName(nameToDisplay);
        setCustomerImage(imageToDisplay);
      }
    } catch (error) {
      console.error("Customer fetch failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- 3. Effects ---
  useEffect(() => {
    // Initial fetch
    if (isLoggedIn) {
      fetchCustomerDetails();
      fetchCartCount();
    } else {
      setCustomerName("");
      setCustomerImage("");
      setCartCount(0);
    }

    // --- THIS IS THE KEY FOR IMMEDIATE UPDATES ---
    // Listen for custom event 'cart-updated'
    const handleCartUpdate = () => fetchCartCount();
    window.addEventListener("cart-updated", handleCartUpdate);

    return () => {
      window.removeEventListener("cart-updated", handleCartUpdate);
    };
  }, [location.pathname, isLoggedIn]); // Added isLoggedIn dependency

  // ... (Rest of your click outside logic and handlers remain the same) ...

  // Close Dropdown on Click Outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsProfileOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const handleLogout = () => {
    sessionStorage.clear();
    setCustomerName("");
    setCustomerImage("");
    setCartCount(0);
    setIsOpen(false);
    setIsProfileOpen(false);
    navigate("/login");
  };

  const handleImageError = (e) => {
    e.target.style.display = "none";
    const sibling = e.target.nextElementSibling;
    if (sibling) sibling.style.display = "flex";
  };

  const getInitials = (name) => {
    if (!name || name === "User") return "U";
    return name.charAt(0).toUpperCase();
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 bg-white shadow-sm z-50">
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-[5%]">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <Link to="/">
              <img src={logo} alt="Logo" className="w-[50%]" />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-10">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`font-semibold text-lg ${
                    location.pathname === link.path
                      ? "text-blue-700 border-b-2 border-blue-700"
                      : "text-blue-900 hover:text-blue-700"
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              {/* Cart Icon */}
              <Link to="/cart" className="relative">
                <FaShoppingCart className="w-7 h-7 text-blue-900 hover:text-blue-700 transition" />
                {/* LOGIC UPDATE: Show badge if count > 0, otherwise nothing (or you can show 0) */}
                {cartCount > 0 && (
                  <span className="absolute -top-1 -right-1  text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center bg-gradient-to-r from-[#9ed8e1] to-[#f49cb9] text-white shadow-md hover:scale-105 transition-transform duration-200">
                    {cartCount}
                  </span>
                )}
              </Link>

              {/* USER SECTION */}
              {isLoggedIn ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setIsProfileOpen(!isProfileOpen)}
                    className="flex items-center gap-2 hover:bg-gray-50 px-2 py-1 rounded-lg transition"
                  >
                    {/* ... User Button Code (Same as before) ... */}
                    {isLoading && !customerName ? (
                      <div className="h-10 w-10 bg-gray-200 rounded-full animate-pulse"></div>
                    ) : (
                      <>
                        {customerImage ? (
                          <img
                            src={customerImage}
                            alt="User"
                            className="h-9 w-9 rounded-full object-cover border"
                            onError={handleImageError}
                          />
                        ) : (
                          <div className="h-9 w-9 bg-blue-900 text-white flex items-center justify-center rounded-full font-bold text-sm">
                            {getInitials(customerName)}
                          </div>
                        )}
                        <span className="font-semibold text-blue-900 text-base max-w-[100px] truncate">
                          {customerName || "User"}
                        </span>
                        <FaChevronDown
                          className={`text-xs text-gray-500 transition-transform ${
                            isProfileOpen ? "rotate-180" : ""
                          }`}
                        />
                      </>
                    )}
                  </button>

                  {/* Dropdown Menu */}
                  {isProfileOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-lg border border-gray-100 py-2 animate-in fade-in zoom-in duration-200">
                      <div className="px-4 py-2 border-b border-gray-100 mb-1">
                        <p className="text-xs text-gray-500">Signed in as</p>
                        <p className="text-sm font-bold text-gray-900 truncate">
                          {customerName}
                        </p>
                      </div>
                      <Link
                        to="/my-orders"
                        className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <FaBoxOpen /> My Orders
                      </Link>
                       <Link
                        to="/profile"
                        className="flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-blue-50 hover:text-blue-700 transition"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <FaBoxOpen /> Profile
                      </Link>
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2 text-red-600 hover:bg-red-50 transition text-left"
                      >
                        <FaSignOutAlt /> Logout
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  to="/login"
                  className="bg-blue-900 hover:bg-blue-800 text-white px-6 py-2.5 rounded-full shadow-md transition"
                >
                  Login
                </Link>
              )}
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="md:hidden p-2 text-blue-900"
            >
              {isOpen ? <HiX size={26} /> : <HiMenu size={26} />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isOpen && (
          <div className="md:hidden bg-white border-t shadow-lg">
            {/* ... Mobile Menu Links ... */}
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                onClick={() => setIsOpen(false)}
                className="block px-6 py-3 text-lg font-semibold text-blue-900 hover:bg-gray-50"
              >
                {link.name}
              </Link>
            ))}

            <Link
              to="/cart"
              onClick={() => setIsOpen(false)}
              className="block px-6 py-3 text-lg font-semibold text-blue-900 hover:bg-gray-50 flex justify-between items-center"
            >
              <span>My Cart</span>
              {cartCount > 0 && (
                <span className="bg-pink-500 text-white text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center">
                  {cartCount}
                </span>
              )}
            </Link>

            {/* Mobile User Section (Same as before) */}
            <div className="px-6 py-4 border-t bg-gray-50">
              {isLoggedIn ? (
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    {customerImage ? (
                      <img
                        src={customerImage}
                        alt="User"
                        className="h-10 w-10 rounded-full object-cover border"
                        onError={handleImageError}
                      />
                    ) : (
                      <div className="h-10 w-10 bg-blue-900 text-white flex items-center justify-center rounded-full font-bold text-lg">
                        {getInitials(customerName)}
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-blue-900">
                        {customerName || "User"}
                      </p>
                      <Link
                        to="/my-orders"
                        onClick={() => setIsOpen(false)}
                        className="text-sm text-gray-500 hover:text-blue-600 flex items-center gap-1"
                      >
                        <FaBoxOpen className="text-xs" /> View My Orders
                      </Link>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full bg-white border border-red-200 text-red-600 px-4 py-2 rounded-lg font-medium hover:bg-red-50 transition flex items-center justify-center gap-2"
                  >
                    <FaSignOutAlt /> Logout
                  </button>
                </div>
              ) : (
                <Link
                  to="/login"
                  onClick={() => setIsOpen(false)}
                  className="block w-full bg-blue-900 text-white text-center py-2.5 rounded-lg font-semibold shadow"
                >
                  Login
                </Link>
              )}
            </div>
          </div>
        )}
      </header>
      <div className="pt-20" />
    </>
  );
};

export default Header;
